import { useCallback, useEffect, useState } from 'react';

import { useFormatQuantity } from '@/resources/hooks/useFormatQuantity';
import { ICartProduct, IOrder } from '@/store/ducks/order/types';
import { IProduct } from '@/store/ducks/products/types';

interface UseCartItemStateProps {
  product: IProduct | ICartProduct | null;
  order: IOrder | null;
  currentMeasure: 'kg' | 'unity';
}

export const useCartItemState = ({ product, order, currentMeasure }: UseCartItemStateProps) => {
  const getCartItem = useCallback(() => {
    if (!product || !order?.cart.products) return null;

    const cartItem = order.cart.products.find((cartItem) => {
      if (product.uid) {
        return cartItem.uid === product.uid;
      }
      const basicMatch = cartItem.product_id === product.product_id && cartItem.ean === product.ean;

      if ((product as ICartProduct).combo_seq_id) {
        return basicMatch && cartItem.combo_seq_id === (product as ICartProduct).combo_seq_id;
      }

      return basicMatch && !cartItem.combo_seq_id;
    });

    return cartItem ?? null;
  }, [product, order]);

  const [cartItem, setCartItem] = useState<ICartProduct | null>(getCartItem());
  const [action, setAction] = useState<'add' | 'receive'>(cartItem ? 'receive' : 'add');

  const getInitialQuantity = useCallback(() => {
    if (typeof cartItem !== 'undefined' && cartItem) return cartItem.quantity;
    return 1;
  }, [cartItem]);

  const initialQuantity = useFormatQuantity({
    currentMeasure,
    quantity: getInitialQuantity(),
    seeAsUnit: product?.see_as_unit ?? false,
  });

  useEffect(() => setCartItem(getCartItem()), [getCartItem]);
  useEffect(() => setAction(cartItem ? 'receive' : 'add'), [cartItem]);

  return {
    cartItem,
    action,
    getInitialQuantity,
    initialQuantity,
  };
};
